import React from 'react'
import "../../styles/ApprovalConfiguration.css"
import ApprovalHeader from './ApprovalHeader'
import DatePicker from "../DatePicker"
import { useState, useEffect } from 'react'
import ReactPaginate from "react-paginate";
import Vector from "../../assets/svg/editIcon.svg";
import Delete from "../../assets/svg/DeleteIcon.svg";
import { useNavigate } from 'react-router-dom'
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify"
import axiosInstance from '../../axiosInstance'
import { usePermissions } from '../../hooks/PermissionContext'
import { useModule } from '../../hooks/ModuleContext'
import PermissionWrapper from '../../hooks/PermissionWrapper'
import ConfirmDialog from '../../ConfirmDialog/ConfirmDialog'
import CommonFooter from '../../CommonFooter/CommonFooter'
import BulkApprovalConfig from './BulkApprovalConfig'
import CommonPagination from '../../CommonPagination/CommonPagination'


const ApprovalConfiguration = () => {

  const { activeModuleId } = useModule();
  const { getModulePermission } = usePermissions();
  const permission = getModulePermission(activeModuleId);
  console.log("activeModuleId", activeModuleId);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableData, setTableData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [limit, setLimit] = useState(5);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState("dateCreated");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [bulkApprovalOpen, setApprovalBulkOpen] = useState(false);
  const [expandedApproval, setExpandedApproval] = useState(null);


  const navigate = useNavigate();

  useEffect(() => {
    fetchApprovalConfigurations(searchTerm);
  }, [limit, skip]);

  const fetchApprovalConfigurations = async (term = '') => {
    setLoading(true);
    setError(null);
    try {

      const response = await axiosInstance.post(
        `/approval_config/list`,
        {
          limit,
          skip,
          sortBy,
          search: term || ''
        }
      );


      if (response.data && response.data.success) {
        setTableData(response.data.approval_config || []);
        setTotalCount(response.data.totalCount || 0);
        setTotalPages(Math.ceil(response.data.totalCount / limit));
      } else {
        throw new Error(response.data.message || 'Failed to fetch data');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch approval configurations');
      console.error('Error fetching approval configurations:', err);
    } finally {
      setLoading(false);
    }
  };


  const handlePageClick = ({ selected }) => {
    const newSkip = selected * limit;
    setCurrentPage(selected);
    setSkip(newSkip);
    setSelectedRows([]);
  };

  const handleDateSelection = (date) => {
    console.log("Selected Date:", date);
  };

  const isAllSelected = selectedRows.length === tableData.length && tableData.length > 0;
  const isAnySelected = selectedRows.length > 0;

  const handleCheckboxChange = (e, id) => {
    if (e.target.checked) {
      setSelectedRows([...selectedRows, id]);
    } else {
      setSelectedRows(selectedRows.filter(rowId => rowId !== id));
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(tableData.map(row => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleDeleteRow = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    try {

      const response = await axiosInstance.delete(
        `/approval_config/delete/${deleteId}`
      );

      if (response.data && response.data.success) {
        toast.success("Approval configuration deleted successfully!");
        fetchApprovalConfigurations();
        setConfirmOpen(false);
        setDeleteId(null);
      } else {
        throw new Error(response.data.message || 'Failed to delete');
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(`Failed to delete the configuration! ${error.message}`);
    }
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };


  const handleDeleteSelected = async () => {
    const confirmDelete = window.confirm(`Are you sure you want to delete ${selectedRows.length} items?`);
    if (!confirmDelete) return;

    try {
      // const deletePromises = selectedRows.map(id => 
      //   axios.delete(`https://digitallr-uat.flyjaclogistics.com/cost_modelling-be/approval_config/delete/${id}`)
      // );

      const deletePromises = selectedRows.map(id =>
        axiosInstance.delete(`/approval_config/delete/${id}`)
      );

      await Promise.all(deletePromises);
      toast.success("Selected configurations deleted successfully!");
      fetchApprovalConfigurations(); // Refresh the data
      setSelectedRows([]);
    } catch (error) {
      console.error("Error:", error);
      toast.error(`Failed to delete selected configurations! ${error.message}`);
    }
  };

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setLimit(newLimit);
    setCurrentPage(0);
    setSkip(0);
  };

  const handleAddNewClick = () => {
    navigate("/approval_creation");
  };


  const handleEditClick = async (config) => {
    try {
      setLoading(true);

      const response = await axiosInstance.get(
        `/approval_config/edit/${config.id}`
      );

      if (response.data && response.data.success) {
        navigate('/approval_creation', {
          state: {
            config: response.data.data,
            isEditMode: true
          }
        });
      } else {
        throw new Error(response.data.message || 'Failed to fetch configuration');
      }
    } catch (error) {
      console.error("Error fetching configuration:", error);
      toast.error("Failed to load configuration: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (term) => {
    setSearchTerm(term);
    setSkip(0);
    await fetchApprovalConfigurations(term);
  };

  const toggleApprovalLevel = (rowId, level) => {
    setExpandedApproval(prev => {
      if (prev?.rowId === rowId && prev?.level === level) {
        return null; 
      }
      return { rowId, level };
    });
  };

  const handleBulkSuccess = () => {
    setSkip(0);
    setCurrentPage(0);
    fetchApprovalConfigurations(searchTerm);
    setApprovalBulkOpen(false);
  };


  return (
    <>
      <ApprovalHeader onSearch={handleSearch} />
      <div className='approval-config'>
        <div className='approval-header'>
          <div className='approval-left-header'>
            <p>All Approval Projects ({totalCount})</p>
          </div>
          <div className='approval-right-header'>
            {/* {!isAnySelected ? (
              <>
                <DatePicker onDateChange={handleDateSelection} />

                <h4>Sort by :</h4>
                <div className="dropdown">
                  <select id="options" className="dropdown-select">
                    <option value="option1">Date Created</option>
                    <option value="option2">Project Name</option>
                    <option value="option3">Module</option>
                  </select>
                </div>
              </>
            ) : (
              <button id="deleteBtn" className="deleteBtn" onClick={handleDeleteSelected}>
                Delete Selected
              </button>
            )} */}

            <button
              id="addNewBtn"
              style={{ backgroundColor: '#097BED', fontWeight: '600' }}
              onClick={() => setApprovalBulkOpen(true)}
            >
              Bulk Assign
            </button>

            <PermissionWrapper permissionKey="create_permission">
              {!isAnySelected && (
                <button id="addNewBtn" onClick={handleAddNewClick}>
                  Add New
                </button>
              )}
            </PermissionWrapper>

          </div>
        </div>


        <div className="approval-config-table" style={{ overflowX: 'auto', whiteSpace: 'nowrap', scrollbarWidth: 'none', OverflowStyle: 'none', WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth' }}>
          <table>
            <thead>
              <tr>
                {/* <th style={{ width: "1px" }}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </th> */}
                <th style={{ textAlign: "center" }}>S.No</th>
                <th>Project Name</th>
                <th>Module</th>
                <th>Billing Type</th>
                <th>Approval Flow</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tableData.length > 0 ? (
                tableData.map((item, index) => {
                  // Map module numbers to their corresponding names
                  const getModuleName = (moduleNumber) => {
                    switch (moduleNumber) {
                      case '1': return 'Contract';
                      case '2': return 'Job';
                      case '3': return 'Invoice';
                      default: return 'N/A';
                    }
                  };

                  return (
                    <tr key={item.id}>
                      {/* <td style={{ width: "1px" }}>
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(item.id)}
                          onChange={(e) => handleCheckboxChange(e, item.id)}
                        />
                      </td> */}
                      <td style={{ textAlign: "center" }}> {skip + index + 1}</td>
                      <td>{item.project_name || 'N/A'}</td>
                      <td>{getModuleName(item.module)}</td>
                      <td>{item.billing_type}</td>
                      <td>
                        <div className="approval-flow-levels">
                          {item.approval_levels?.map((level, lvlIdx) => {
                            const isExpanded =
                              expandedApproval?.rowId === item.id &&
                              expandedApproval?.level === level.level;

                            return (
                              <div key={lvlIdx} className="approval-flow-level-block">
                                {/* Level Header */}
                                <div className="approval-flow-level-pill">
                                  <span>Level {level.level}</span> -
                                  <span className="approval-flow-count-badge">
                                    {level.users.length}
                                  </span>
                                  <div className="approval-flow-hover-drawer">
                                    <strong>Level {level.level} Users</strong>
                                    <div className="approval-flow-hover-users">
                                      {level.users.map((user) => (
                                        <span key={user.id} className="approval-flow-hover-emp">
                                          {user.name}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>

                                {/* COLLAPSED STATE */}
                                {!isExpanded && (
                                  <div
                                    className="approval-flow-avatars clickable"
                                    onClick={() => toggleApprovalLevel(item.id, level.level)}
                                  >
                                    {level.users.slice(0, 4).map((u, i) => (
                                      <div className="approval-flow-avatar" key={i}>
                                        {u.name.charAt(0).toUpperCase()}
                                      </div>
                                    ))}

                                    {level.users.length > 4 && (
                                      <div className="approval-flow-avatar approval-flow-avatar-more">
                                        +{level.users.length - 4}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* EXPANDED STATE */}
                                {isExpanded && (
                                  <div className="approval-flow-expanded">
                                    {level.users.map((user) => (
                                      <span key={user.id} className="approval-flow-emp">
                                        {user.name}
                                      </span>
                                    ))}
                                    <span
                                      className="collapse-link"
                                      onClick={() => toggleApprovalLevel(item.id, level.level)}
                                    >
                                      X
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </td>

                      <td>
                        <div className="actions" style={{ gap: "10px" }}>

                          <PermissionWrapper permissionKey="edit_permission">
                            <span
                              className="edit-icon"
                              style={{
                                cursor: "pointer",
                                width: "30px",
                                height: "30px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "50%",
                                backgroundColor: "#fff",
                              }}
                              onClick={() => handleEditClick(item)}
                              disabled={loading}
                            >
                              <img src={Vector} alt="Edit" width="14" height="14" />
                            </span>
                          </PermissionWrapper>

                          <PermissionWrapper permissionKey="delete_permission">
                            <span
                              className="delete-icon"
                              style={{
                                cursor: "pointer",
                                width: "30px",
                                height: "30px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "50%",
                                backgroundColor: "#fff",
                              }}
                              onClick={() => handleDeleteRow(item.id)}
                            >
                              <img src={Delete} alt="Delete" width="27" height="27" />
                            </span>
                          </PermissionWrapper>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  {searchTerm && tableData.length === 0 && (
                    <td colSpan="7" style={{ textAlign: 'center', padding: '20px', fontSize: '14px', color: '#666' }}>
                      No ApprovalConfiguration found matching "{searchTerm}"
                    </td>
                  )}
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <CommonPagination
          currentPage={currentPage}
          totalPages={totalPages}
          limit={limit}
          onPageChange={handlePageClick}
          onLimitChange={handleLimitChange}
        />
      </div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <ConfirmDialog
        open={confirmOpen}
        title="Delete Confirmation"
        message="Are you sure you want to delete this Approval Config?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
      <CommonFooter />
      <BulkApprovalConfig
        open={bulkApprovalOpen}
        onClose={() => setApprovalBulkOpen(false)}
         onSuccess={handleBulkSuccess}
      />
    </>
  )
}

export default ApprovalConfiguration